
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useStaffMember, useUpdateStaff, type UserType } from '@/api/staff';
import { useGym } from '@/contexts/gym-context';
import { Skeleton } from '@/components/ui/skeleton';

interface EditStaffDialogProps {
  staffId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const formSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  surname: z
    .string()
    .min(2, { message: 'Surname must be at least 2 characters' }),
  email: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Please enter a valid email address',
    }),
  phone: z.string().optional(),
  userType: z.enum(['admin', 'reception', 'trainer', 'staff']),
  status: z.enum(['active', 'inactive']),
  gym: z.string().min(1, { message: 'Please select at least one gym' }),
});

type FormValues = z.infer<typeof formSchema>;

export function EditStaffDialog({
  staffId,
  open,
  onOpenChange,
  onSuccess,
}: EditStaffDialogProps) {
  const { t } = useTranslation();
  const { data: staff, isLoading, error: fetchError } = useStaffMember(staffId);
  const { mutateAsync: updateStaff, isPending } = useUpdateStaff();
  const { gyms } = useGym();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      userType: 'trainer',
      status: 'active',
      gym: '',
    },
  });

  useEffect(() => {
    if (staff) {
      form.reset({
        name: staff.name || '',
        surname: staff.surname || '',
        email: staff.email || '',
        phone: staff.phone || '',
        userType: (staff.userType as UserType) || 'trainer',
        status: staff.status || 'active',
        gym: staff.gym && staff.gym.length > 0 ? staff.gym[0] : '',
      });
    }
  }, [staff, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      setError(null);
      await updateStaff({
        id: staffId,
        ...data,
        userType: data.userType as UserType,
        gym: [data.gym],
      });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update staff member'
      );
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (fetchError || !staff) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="text-center py-4 text-red-500">
            <p>{t('staff.errorLoading')}</p>
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="mt-2"
            >
              {t('common.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('staff.editStaff')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('staff.firstName')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('staff.firstNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('staff.lastName')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('staff.lastNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('staff.emailPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.phone')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('staff.phonePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="userType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.userType')}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('staff.selectUserType')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="staff">
                        {t('staff.userTypes.staff')}
                      </SelectItem>
                      <SelectItem value="admin">
                        {t('staff.userTypes.admin')}
                      </SelectItem>
                      <SelectItem value="reception">
                        {t('staff.userTypes.reception')}
                      </SelectItem>
                      <SelectItem value="trainer">
                        {t('staff.userTypes.trainer')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.status')}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('staff.selectStatus')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">
                        {t('staff.active')}
                      </SelectItem>
                      <SelectItem value="inactive">
                        {t('staff.inactive')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gym"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('staff.gyms')}</FormLabel>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    {gyms && gyms.length > 0 ? (
                      gyms.map((gym) => (
                        <div
                          key={gym._id}
                          className="flex items-center space-x-3"
                        >
                          <RadioGroupItem
                            value={gym._id}
                            id={`gym-${gym._id}`}
                          />
                          <FormLabel
                            htmlFor={`gym-${gym._id}`}
                            className="font-normal cursor-pointer"
                          >
                            {gym.name}
                          </FormLabel>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t('staff.noGymsAvailable')}
                      </p>
                    )}
                  </RadioGroup>
                  <FormMessage />
                </FormItem>
              )}
            />

            {error && <div className="text-red-500 text-sm">{error}</div>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
